import { supabase } from "@/integrations/supabase/client";

const CHUNK = 150;

export interface TaskChecklistAssignmentRow {
  checklist_id: string;
  checklist_name: string;
  assigned_type: string;
  cleaner_id: string | null;
  assignee_user_id: string | null;
  assignee_label: string;
}

type RawAssignmentRow = {
  cleaning_task_id: string;
  checklist_id: string;
  assigned_type: string;
  cleaner_id: string | null;
  assignee_user_id: string | null;
};

async function fetchRawRowsForTaskIds(taskIds: string[]): Promise<RawAssignmentRow[]> {
  const allRows: RawAssignmentRow[] = [];
  if (taskIds.length === 0) return allRows;
  for (let i = 0; i < taskIds.length; i += CHUNK) {
    const slice = taskIds.slice(i, i + CHUNK);
    const { data, error } = await supabase
      .from("cleaning_task_checklist_assignments")
      .select("cleaning_task_id, checklist_id, assigned_type, cleaner_id, assignee_user_id")
      .in("cleaning_task_id", slice);
    if (error) throw error;
    allRows.push(...((data ?? []) as RawAssignmentRow[]));
  }
  return allRows;
}

function buildRows(
  raw: Omit<RawAssignmentRow, "cleaning_task_id">[],
  checklistMap: Map<string, string>,
  cleanerMap: Map<string, string>,
  userMap: Map<string, string>,
): TaskChecklistAssignmentRow[] {
  return raw.map((r) => {
    let assignee_label = "Unassigned";
    if (r.assigned_type === "cleaner" && r.cleaner_id) {
      assignee_label = cleanerMap.get(r.cleaner_id) ?? "Cleaner";
    } else if ((r.assigned_type === "team" || r.assigned_type === "owner") && r.assignee_user_id) {
      assignee_label = userMap.get(r.assignee_user_id) ?? "Team";
    }
    return {
      checklist_id: r.checklist_id,
      checklist_name: checklistMap.get(r.checklist_id) ?? "Checklist",
      assigned_type: r.assigned_type,
      cleaner_id: r.cleaner_id,
      assignee_user_id: r.assignee_user_id,
      assignee_label,
    };
  });
}

async function loadLookupMaps(raw: RawAssignmentRow[]) {
  const checklistIds = [...new Set(raw.map((r) => r.checklist_id))];
  const cleanerIds = [...new Set(raw.map((r) => r.cleaner_id).filter(Boolean))] as string[];
  const userIds = [...new Set(raw.map((r) => r.assignee_user_id).filter(Boolean))] as string[];

  const [checklistRes, cleanersRes, profilesRes] = await Promise.all([
    checklistIds.length
      ? supabase.from("checklists").select("id, name").in("id", checklistIds)
      : { data: [] as { id: string; name: string }[] },
    cleanerIds.length
      ? supabase.from("cleaners").select("id, name").in("id", cleanerIds)
      : { data: [] as { id: string; name: string }[] },
    userIds.length
      ? supabase.from("profiles").select("id, full_name").in("id", userIds)
      : { data: [] as { id: string; full_name: string | null }[] },
  ]);

  return {
    checklistMap: new Map((checklistRes.data ?? []).map((c) => [c.id, c.name])),
    cleanerMap: new Map((cleanersRes.data ?? []).map((c) => [c.id, c.name])),
    userMap: new Map((profilesRes.data ?? []).map((p) => [p.id, p.full_name ?? "Team"])),
  };
}

/** Per-checklist assignments for one task. */
export async function fetchTaskChecklistAssignmentRows(
  taskId: string,
): Promise<TaskChecklistAssignmentRow[]> {
  const raw = await fetchRawRowsForTaskIds([taskId]);
  if (!raw.length) return [];
  const maps = await loadLookupMaps(raw);
  return buildRows(raw, maps.checklistMap, maps.cleanerMap, maps.userMap);
}

/** Per-checklist assignments keyed by cleaning_task_id (batch for schedule views). */
export async function fetchTaskChecklistAssignmentsByTaskId(
  taskIds: string[],
): Promise<Map<string, TaskChecklistAssignmentRow[]>> {
  const byTask = new Map<string, TaskChecklistAssignmentRow[]>();
  const raw = await fetchRawRowsForTaskIds(taskIds);
  if (!raw.length) return byTask;

  const maps = await loadLookupMaps(raw);

  for (const r of raw) {
    const [row] = buildRows([r], maps.checklistMap, maps.cleanerMap, maps.userMap);
    const list = byTask.get(r.cleaning_task_id) ?? [];
    list.push(row);
    byTask.set(r.cleaning_task_id, list);
  }

  return byTask;
}
